import React, { useState } from 'react';
import useMediaQuery from "@mui/system/useMediaQuery";
import { IconButton, Typography, useTheme } from "@mui/joy";
import Dialog from "@mui/material/Dialog";
import { CSS } from "@dnd-kit/utilities";
import { BasicModalContent } from "@dreebit-registry/vsm-microservice-components";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { GridAsset, Maybe } from "../../../codegen/graphql";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { DragHandleRounded, VisibilityFilledRounded, VisibilityOffFilledRounded } from "@mui-symbols-material/w300";
import { closestCenter, DndContext } from "@dnd-kit/core";
import { UpdateGridAsset } from "../../../graphql/mutations/updateGridAsset";
import { UpdateGridAssetRank } from "../../../graphql/mutations/updateGridAssetRank";
import {restrictToParentElement, restrictToVerticalAxis} from "@dnd-kit/modifiers";
import { Box } from '@mui/material';

interface Props {
    gridAssets: Maybe<GridAsset>[];
    title?: string | null;
    open: boolean;
    onClose?: () => void;
}

interface SortableItemProps {
    id: string;
    title?: string | null;
    visible?: boolean | null;
    onToggleVisibility: () => void;
    theme?: any
}

const SortableItem = ({ id, title, visible, onToggleVisibility, theme }: SortableItemProps) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        display: "flex",

        justifyContent: "space-between",
        padding: "8px 12px",
        borderBottom: "1px solid #ccc",
        background: "white",
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <Box display={"flex"} alignItems={"center"} flexGrow={1}>
                <IconButton
                    size="sm"
                    onClick={onToggleVisibility}
                    sx={{color: visible ? theme?.palette?.primary[500] : "#ccc"}}
                >
                    {visible ? <VisibilityFilledRounded /> : <VisibilityOffFilledRounded />}
                </IconButton>
                <Typography level="titleSmall" sx={{pl: "12px"}}>
                    {title ?? 'No Title'}
                </Typography>
            </Box>
            <Box sx={{cursor: "pointer"}} {...listeners}>
                <DragHandleRounded />
            </Box>

        </div>
    );
};

const EditGridItemModal = ({ gridAssets, title, onClose, open }: Props) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation();

    // Nur GridAssets ohne null/undefined in State nehmen & nach sortId sortieren
    const [items, setItems] = useState<GridAsset[]>(() =>
        gridAssets.filter((a): a is GridAsset => a != null)
            .sort((a, b) => (a.sortId ?? 0) - (b.sortId ?? 0))
    );

    const [updateGridAssetMutation] = useMutation(UpdateGridAsset);
    const [updateGridAssetRankMutation] = useMutation(UpdateGridAssetRank);

    const handleDragEnd = (event: any) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);

        setItems(newItems); // neue Reihenfolge für UI

        updateGridAssetRankMutation({
            variables: {
                id: active.id,
                beforeGridAssetId: newItems[newIndex + 1]?.id || null,
                afterGridAssetId: newItems[newIndex - 1]?.id || null,
            },
            refetchQueries: ["GridDetail"],
        });
    };

    const toggleVisibility = (id: string) => {
        const asset = items.find((i) => i.id === id);
        if (!asset) return;

        updateGridAssetMutation({
            variables: { id, input: { visible: !asset.visible } },
        });

        setItems((prev) =>
            prev.map((i) => (i.id === id ? { ...i, visible: !i.visible } : i))
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullScreen={isFullScreen}>
            <BasicModalContent
                onClose={onClose}
                title={<Typography level={'titleSmallLight'}>{t('Edit')}: <Typography level={'titleSmall'}>{title}</Typography></Typography>}
            >
                <Box sx={{pt: "12px", pb: "12px"}}>
                    <DndContext
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                    >
                        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                            {items.map((item) => (
                                <SortableItem
                                    key={item.id}
                                    id={item.id}
                                    title={item.title}
                                    visible={item.visible}
                                    onToggleVisibility={() => toggleVisibility(item.id)}
                                    theme={theme}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                </Box>
            </BasicModalContent>
        </Dialog>
    );
};

export default EditGridItemModal;
